import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Typography, 
  Container, 
  IconButton, 
  Box,
  Grid,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
  Paper
} from '@mui/material';
import { Close, Email, Phone, LocationOn } from '@mui/icons-material';

const Contact = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    inquiryType: '',
    message: ''
  });
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would normally send the form data to your backend
    console.log('Form submitted:', formData);
    
    // Show success message
    setSnackbar({
      open: true,
      message: 'Your message has been sent! We\'ll get back to you soon.',
      severity: 'success'
    });
    
    // Reset form
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      inquiryType: '',
      message: ''
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prevState => ({
      ...prevState,
      open: false
    }));
  };

  const contactInfo = [
    {
      icon: <Email />,
      title: 'Email Us',
      content: 'info@searcheble.com',
      description: 'For general inquiries'
    },
    {
      icon: <Phone />,
      title: 'Call Us',
      content: '+1 (555) 123-4567',
      description: 'Monday-Friday, 9AM-5PM PST'
    },
    {
      icon: <LocationOn />,
      title: 'Visit Us',
      content: '123 Search Street, San Francisco, CA 94107',
      description: 'By appointment only'
    }
  ];

  const inquiryTypes = [
    'General Inquiry',
    'Sales',
    'Partnership',
    'Support',
    'Careers',
    'Media'
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" color="#0d9488">
            Contact Us
          </Typography>
          <IconButton onClick={onClose} aria-label="close">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Container>
          <Typography paragraph>
            Have questions or need more information? Our team is here to help. Fill out the form below and we'll get back to you as soon as possible.
          </Typography>

          <Grid container spacing={4} sx={{ mt: 2 }}>
            {/* Contact Form */}
            <Grid item xs={12} md={7}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Inquiry Type</InputLabel>
                      <Select
                        value={formData.inquiryType}
                        onChange={handleChange}
                        label="Inquiry Type"
                        name="inquiryType"
                        required
                      >
                        {inquiryTypes.map((type) => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Your Message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      multiline
                      rows={4}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      sx={{
                        mt: 2,
                        bgcolor: '#0d9488',
                        '&:hover': { bgcolor: 'rgba(13, 148, 136, 0.8)' }
                      }}
                    >
                      Send Message
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} md={5}>
              <Box sx={{ height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Contact Information
                </Typography>
                <Typography paragraph>
                  Reach out to us directly or use the form to send us a message.
                </Typography>
                
                <Box sx={{ mt: 4 }}>
                  {contactInfo.map((info, index) => (
                    <Paper key={index} elevation={0} sx={{ p: 2, mb: 3, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Box sx={{ 
                          mr: 2, 
                          color: '#0d9488',
                          bgcolor: 'rgba(13, 148, 136, 0.1)',
                          borderRadius: '50%',
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {info.icon}
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" gutterBottom>
                            {info.title}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {info.content}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {info.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>

        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </DialogContent>
    </Dialog>
  );
};

export default Contact;